import Styles from './Table.scss';

import Template from './Table.hbs';
import Util from 'util/util'
import InputCell from './InputCell.hbs'

export default class Template39Table extends BaseView {

    static get validGapTypes() {
        return ['radio', 'checkbox']
    }

    get gapType() {
        const firstGapCell = this.tableData.find((cell) => cell.isGapCell && cell.gapType)
        if (firstGapCell && Template39Table.validGapTypes.includes(firstGapCell.gapType)) {
            return firstGapCell.gapType
        }
        return 'radio'
    }

    get gapCells() {
        return this.tableData.filter((cell) => cell.isGapCell)
    }

    initialize({
        tableData = [],
        response
    }) {

        this.tableData = tableData
        this.response = response

        this.setElement(this.generateTemplate(tableData))

        this.setupTable()

    }

    getNumberOfColumns(tableData) {
        const maxColumnIndex = _.max(tableData, 'columnIndex').columnIndex
        return maxColumnIndex ? maxColumnIndex + 1 : 0
    }

    /**
     * Transforms input table data into a table which is rectangular, meaning it each row has the
     * same number of columns and visa-versa. This prevents Table from rendering a "ragged" table
     * where missing empty cells make other cells allign incorrectly.
     *
     * @param {Object} tableData        original table data object
     * @returns {Object} transformed    tableData object
     */
    getRectangularTableData(tableData) {
        const numberOfColumns = this.getNumberOfColumns(tableData)
        const tableRows = []
        for (const cell of tableData) {
            // For the total number of columns, create a cell for each in the current row,
            // then replace this cell with one from the data if present at that row and column index.
            if (Array.isArray(tableRows[cell.rowIndex]) === false) {
                tableRows[cell.rowIndex] = _.times(numberOfColumns, (columnIndex) => {
                    return {
                        rowIndex: cell.rowIndex,
                        columnIndex
                    }
                })
            }
            tableRows[cell.rowIndex][cell.columnIndex] = cell
        }
        return _.sortBy(_.sortBy(_.flatten(tableRows).filter(Boolean), 'columnIndex'), 'rowIndex')
    }

    /**
     *
     * @param {Object} tableData        table data object
     * @param {Array} columnWidths      optional array of custom column widths
     * @returns {String} Template to become the DOM representation of the table data
     */
    generateTemplate(tableData, columnWidths = []) {
        const numberOfColumns = this.getNumberOfColumns(tableData)
        const headerColumns = _.times(numberOfColumns, (columnIndex) => {
            let columnWidth = columnWidths[columnIndex] || {}
            if (columnWidth.percentage) {
                columnWidth = `${columnWidth.percentage}%`
            } else if (columnWidth.px) {
                columnWidth = `${columnWidth.px}px`
            } else {
                columnWidth = false
            }
            return {
                columnIndex,
                columnWidth
            }
        })

        return Template({
            Styles,
            tableRows: Object.values(_.groupBy(this.tableData, 'rowIndex')),
            headerColumns,
            cid: this.cid,
        })
    }

    /**
     * Applies content and properties to the table element.
     *
     */
    setupTable() {

        for (const cell of this.tableData) {

            this.applyCellElementStyling(cell)

            this.setCellElementContent(cell)

        }
    }

    /**
     * Get a cell from the data representation of the table
     *
     * @param {Number} rowIndex row
     * @param {Number} columnIndex column
     * @returns {Object} cell data object
     */
    getCell(rowIndex, columnIndex) {
        // Allow loose comparision since the indexes in the response may be saved as strings instead of numbers.
        // eslint-disable-next-line eqeqeq
        return this.tableData.find(c => c.rowIndex == rowIndex && c.columnIndex == columnIndex)
    }

    /**
     * Get a cell element (TD) from the DOM
     *
     * @param {Number} rowIndex row
     * @param {Number} columnIndex column
     * @returns {HTMLTableCellElement} table cell element
     */
    getCellElement(rowIndex, columnIndex) {
        return this.el.querySelector(Template39Table.getCellElementSelector(rowIndex, columnIndex))
    }

    static getCellElementSelector(rowIndex, columnIndex) {
        return `table tr[data-row="${rowIndex}"] td[data-column="${columnIndex}"]`
    }

    /**
     * Set cell element content based on its corresponding cell data object.
     * If the cell is a gap, make this an input cell if it wasn't already and apply a state to
     * depending on the showAnswer and isReadOnly flags.
     *
     * @param {Object} cell cell data
     * @param {Boolean} showAnswer show correct answer for this gap
     * @param {Boolean} isReadOnly if true, set cell input element to a disabled state
     */
    setCellElementContent(cell, showAnswer, isReadOnly) {
        const cellElement = this.getCellElement(cell.rowIndex, cell.columnIndex)
        if (cell.isGapCell) {
            const gapCellInput = cellElement.querySelector('input')
            if (!gapCellInput) {
                cellElement.outerHTML = InputCell({
                    cell,
                    cid: this.cid,
                })
                this.setCellElementContent(cell, showAnswer, isReadOnly)
                return
            }
            gapCellInput.disabled = isReadOnly
            cellElement.dataset.isGap = true
            if (showAnswer) {
                gapCellInput.checked = cell.cellText
            } else if (this.response) {
                const cellResponse = this.response.find(c => c.row === cell.rowIndex && c.column === cell.columnIndex)
                gapCellInput.checked = cellResponse?.text ?? false
            }
        } else {
            delete cellElement.dataset.gapType
            delete cellElement.dataset.isGap
            cellElement.innerHTML = Util.renderContentSafely(cell.cellText) || ''
        }
    }

    /**
     * Apply styling to the cell element based on the formattings array of corresponding cell data
     *
     * @param {Object} cell cell data
     */
    applyCellElementStyling(cell) {

        if (!Array.isArray(cell.formattings)) {
            return
        }

        const cellElement = this.getCellElement(cell.rowIndex, cell.columnIndex)

        // Remove existing classes and apply classes from formattings array
        cellElement.classList.remove(...cellElement.classList)
        for (const formatting of cell.formattings) {
            cellElement.classList.add(Styles[formatting])
        }

        if (cell.formattings.includes('is-merged')) {
            const previousCellElementInRow = this.getCellElement(cell.rowIndex, cell.columnIndex - 1)
            if (previousCellElementInRow) {
                previousCellElementInRow.colSpan = 2
            }
        }

    }
}
